<script>

	import { onMount, tick } from 'svelte';
	import { fade } from 'svelte/transition';

	// import * as Sentry from "@sentry/browser";

	import moment from 'moment-timezone';

	import cssVars from 'svelte-css-vars';
	import { colord } from "colord";

	import InstallMessage from './InstallMessage.svelte';
	import Settings from './Settings.svelte';
	import Masthead from './Masthead.svelte';
	import Now from './Now.svelte';
	import Spotlight from './Spotlight.svelte';
	import Holding from './Holding.svelte';

	import Button from './ui/Button.svelte';
	import Modal from './ui/Modal.svelte';
	import Spinner from './ui/Spinner.svelte';
	import BigErrorModal from './ui/BigErrorModal.svelte';

	import {
		busy,
		bigError,
		modal,
		offline,
		offlineErrorShown,
		appElement,
		unlockCode,
		showSettings,
		event,
		settings,
		deviceRef,
		deviceName,
		isNarrowScreen,
		displayLang,
		refusedInstall,
		androidInstallPrompt,
		epoch,
		epochInterval,
		remSize,
		syncChannels,
		attendeeLikes,
		shadowbans,
		agenda,
		syncClient,
		attendee,
		spotlight,
		session
	} from './lib/stores.js';

	import { getServerData, postServerData } from './lib/prelude.js';
	import { db } from "./lib/db.js";
	import { getEpoch, fromUTC, isPast } from "./lib/dt.js";
	import { colorMix, getErrorColor, getSuccessColor, bestContrast, isPale, getBrightness } from './lib/color.js';
	import { isDesktop, isInStandaloneMode } from "./lib/device.js";
	import { preloadImage, randomIntFromInterval } from './lib/utils.js';

	let mounted = false;
	let showInstallMessage = false;

	let refresher;
	let twilioLoaded = false;
	let syncToken;
	let syncChannel;
	let splashDt;
	let themeVars = {};
	let textFont = '';
	let headingsFont = '';
	let backgrounded = null;

	let roomSyncChannel = null;
	let roomSyncData = {};

	onMount(async () => {

		await db.open().catch(() => {
			$bigError = 'The app database is unavailable. This may happen if you’re using private browsing mode.';
			mounted = false;
		});

		if (!$bigError) {

			let relatedApps = [];

	    	if ('getInstalledRelatedApps' in navigator) {
	    		relatedApps = await navigator.getInstalledRelatedApps();
	    	}

			if (!isDesktop() && !(isInStandaloneMode() || $refusedInstall || relatedApps.length)) {
				showInstallMessage = true;
			}

			window.addEventListener('beforeinstallprompt', (e) => {
	  			e.preventDefault();
	  			if (!isDesktop() && !(isInStandaloneMode() || $refusedInstall)) {
	  				setTimeout(() => {
			  			$androidInstallPrompt = e;
			  			showInstallMessage = true;
			  		}, 600);
		  		}
			});

			window.addEventListener('offline', (e) => {
				$offline = true;
			});

			window.addEventListener('online', (e) => {
				$offline = false;
				$offlineErrorShown = 0;
			});

			// 14 Feb 2024 -- likely that on resume, we'd remount??
			document.addEventListener('visibilitychange', checkVisibility, false);

			setIsNarrowScreen();

			$epoch = getEpoch();

			$epochInterval = setInterval(() => {
				if ($epoch % 10) {
					$epoch = $epoch + 1;
				} else {
					$epoch = getEpoch();
				}
			}, 1000);

			if (!$unlockCode) await resetEvent();

			mounted = true;

		}

	});

	async function resetEvent() {

		console.log('resetEvent');

		clearInterval(refresher);

		$unlockCode = '';
		// $screen = undefined;

		if ($syncClient) {
			await $syncClient.shutdown();
			$syncClient = null;
		}

		syncToken = null;
		syncChannel = null;

		db.attendees.clear();

		$settings = {
			deviceName: $deviceName
		};

		themeVars = {
			"bgColor": "#101820",
			"textColor": "#ffffff",
			"accentColor": "#e5077e",
			"labelColor": "#ffffff",
			"blend-x": "#0a0e13",
			"blend-10": "#282f36",
			"blend-20": "#40464d",
			"accentBlend-120": "#b70665"
		};

		$modal = null;

	}

	async function sync(inBackground) {

		if (!$offline) {

			console.log('Getting data for ' + $unlockCode, inBackground);

			clearInterval(refresher);

			if (!inBackground) {
				$busy = true;
			}

			let payload = await getServerData('/onstage/sync');

			if (!$bigError && payload) {

				setEvent(payload.event);

				await handleAuthPayload(payload);

				if (!inBackground) {
					$busy = false;
				}

				setTimeout(() => {
					if ($appElement) {
						$appElement.classList.remove('fade');
					}
				}, 800);

			}

		}

		if ($event && $event.ends && isPast(fromUTC($event.ends))) {
			$modal = {
				message: $event.name + ' has now ended.',
				details: [],
				opts: [
					{
						name: "Lock device",
						f: () => {
							$showSettings = false;
							resetEvent();
						}
					},
				]
			};
		}

	}



	function setEvent(eventData) {

		$event = eventData;

		document.title = $event.name;

		let vars = {};

		['bgColor','textColor','accentColor','buttonTextColor','logoColor','mastheadBgColor','mastheadIconColor','navBgColor','navIconColor'].forEach(c => {
			if ($event.theme.app[c]) vars[c] = $event.theme.colors[$event.theme.app[c]];
		});

		const buttonTextColor = bestContrast(
			vars.accentColor,
			['bgColor', vars.bgColor],
			['textColor', vars.textColor]
		);

		vars.buttonTextColor = vars[buttonTextColor];

		vars.errorColor = generateErrorColor(vars.bgColor, vars);
		vars.successColor = generateSuccessColor(vars.bgColor, vars);

		const base = isPale(vars.bgColor) ? '#ffffff' : '#000000';
		const inverseBase = isPale(vars.bgColor) ? '#000000' : '#ffffff';
		const accentBase = isPale(vars.accentColor) ? '#ffffff' : '#000000';
		// const navBgBase = isPale(vars.navBgColor) ? '#ffffff' : '#000000';
		const errorBase = isPale(vars.errorColor) ? '#ffffff' : '#000000';
		const successBase = isPale(vars.successColor) ? '#ffffff' : '#000000';

		for (const b of [95,90,80,60,40,20,10,5]) {

			let blendKey = 'blend-' + String(b).padStart(2, '0');
			vars[blendKey] = colorMix(vars.textColor, base, b);

			let accentBlendKey = 'accentBlend-' + String(b).padStart(2, '0');
			vars[accentBlendKey] = colorMix(vars.accentColor, base, b);

			let navBlendKey = 'navBlend-' + String(b).padStart(2, '0');
			vars[navBlendKey] = colorMix(vars.navIconColor, vars.navBgColor, b);

		}

		vars['accentBlend-120'] = colorMix(vars.accentColor, accentBase, 80);
		vars['errorBlend-120'] = colorMix(vars.errorColor, errorBase, 80);
		vars['successBlend-120'] = colorMix(vars.successColor, successBase, 80);
		vars['blend-120'] = colorMix(vars.bgColor, inverseBase, 20);
		vars['blend-x'] = colorMix(vars.bgColor, base, 60);

		let tc = $event.theme.colors[$event.theme.app.textColor];
		let hsl = colord(tc).toHsl();
		hsl.l = '15';

		let shadow = colord(hsl).alpha(0.1).toRgbString();
		let paleShadow = colord(hsl).alpha(0.05).toRgbString();
		let darkShadow = colord(hsl).alpha(0.2).toRgbString();
		
		vars.shadow = shadow;
		vars.paleShadow = paleShadow;
		vars.darkShadow = darkShadow;

		let ic = $event.theme.colors[$event.theme.app.navIconColor];
		let ichsl = colord(ic).toHsl();
		ichsl.l = '15';
		let navShadow = colord(ichsl).alpha(0.05).toRgbString();
		vars.navShadow = navShadow;

		themeVars = vars;

		// $section = $event.setup.app.nav[0];

	}

	async function handleAuthPayload(authData) {

		if (authData.syncChannels) {
			$syncChannels = authData.syncChannels;
		}

		if (authData.attendee) {
			$attendee = authData.attendee;
		}

		if (authData.agenda) {
			setAgenda(authData.agenda);
		}

		if (authData.shadowbans) {
			$shadowbans = authData.shadowbans;
		}

		if (authData.likes) {
			$attendeeLikes = authData.likes;
		}

		if (authData.syncToken) {
			syncToken = authData.syncToken;
		}

	}

	function setAgenda(a) {

		console.log('setAgenda', a.sessions ? 'has sessions' : 'no sessions');

		$agenda = a;

		const tz = $event.setup.timeZone;
		const cutoff = moment.utc($event.ends).unix();

		$event.epochEnds = moment.utc($event.starts).unix();

		for (const [i,s] of $agenda.sessions.entries()) {

			s.starts = fromUTC(s.starts,tz);
			s.ends = fromUTC(s.ends,tz);
			s.epochStarts = s.starts.utc().unix();
			s.epochEnds = s.ends.utc().unix();

			if (s.epochEnds > $event.epochEnds) {
				$event.epochEnds = s.epochEnds;
			}

			$agenda.sessions[i] = s;

		}

	}

	function generateErrorColor(basis, vars) {
		if ($event && $event.theme && $event.theme.colors) {
			return getErrorColor([
				basis,
				$event.theme.colors['accent1'],
				$event.theme.colors['bgColor'],
				$event.theme.colors['textColor'],
				$event.theme.colors['accent2'],
				$event.theme.colors['accent3'],
				$event.theme.colors['accent4'],
				$event.theme.colors['accent5'],
				$event.theme.colors['accent6']
			]);
		} else {
			return getErrorColor([
				basis,
				vars['accentColor'],
				vars['bgColor'],
				vars['textColor']
			]);
		}
	}

	function generateSuccessColor(basis, vars) {
		if ($event && $event.theme && $event.theme.colors) {
			return getSuccessColor([
				basis,
				$event.theme.colors['accent1'],
				$event.theme.colors['bgColor'],
				$event.theme.colors['textColor'],
				$event.theme.colors['accent2'],
				$event.theme.colors['accent3'],
				$event.theme.colors['accent4'],
				$event.theme.colors['accent5'],
				$event.theme.colors['accent6']
			]);
		} else {
			return getSuccessColor([
				basis,
				vars['accentColor'],
				vars['bgColor'],
				vars['textColor']
			]);
		}
	}

	function setIsNarrowScreen() {
		$isNarrowScreen = window.matchMedia("screen and (max-width: 640px)").matches;
	}

	async function refreshSyncToken() {
		const refreshData = await getServerData('onstage/auth/refresh');
		if (!$bigError) {
			if (refreshData.syncToken) {
				syncToken = refreshData.syncToken;
				$syncClient.updateToken(syncToken);
				// console.log('Sync token REFRESHED');
			}
		}
	}

	function setTwilioLoaded() {
		twilioLoaded = true;
	}

	async function twilioConnect() {
		if (!$syncClient) {

			$syncClient = new Twilio.Sync.Client(syncToken);

			$syncClient.on('connectionError', (connectionError) => {
				// console.error('Connection was interrupted:', connectionError.message);
				// console.error('Is terminal:', connectionError.terminal);
				if (connectionError.terminal && !$offline) {
					refreshSyncToken();
				}
			});

			$syncClient.on('tokenAboutToExpire', () => {
				// console.log('Sync token about to expire -- RENEWING');
				refreshSyncToken();
			});

			try {

				// console.log('sync -- event');
				let eventNotifications = await $syncClient.list($syncChannels.event);

				eventNotifications.on('itemAdded', syncItemAdded);
				eventNotifications.on('itemRemoved', syncItemRemoved);

			} catch (e) {

				console.log("Sync error", e, $syncChannels.event);

			}

			try {

				// console.log('loading', $syncChannels.attendance);

				console.log('sync -- attendance', $syncChannels.attendance);
				let attendance = await $syncClient.map($syncChannels.attendance);

				attendance.on('itemAdded', (args) => {
					console.log('attendance item added', args.item);
					// $attendees[args.item.key] = args.item.data;
					// $attendees[args.item.key].seq = $attendees[args.item.key].seq ? $attendees[args.item.key].seq : args.item.dateUpdated.getTime();
					// $attendees = $attendees;
					upsertAttendee(args.item);
				});

				attendance.on('itemRemoved', (args) => {
					console.log('attendance item deleted', args);
					// delete $attendees[args.key];
					// $attendees = $attendees;
					db.attendees.delete(args.key);
				});

				attendance.on('itemUpdated', (args) => {
					console.log('attendance item updated', args.item);
					// $attendees[args.item.key] = args.item.data;
					// $attendees = $attendees;
					upsertAttendee(args.item);
				});

				getAttendance(attendance);

			} catch (e) {

				console.log("Sync error", e, $syncChannels.attendance);

			}

			try {

				console.log('sync -- roomSyncChannel', $syncChannels.onstage);
				roomSyncChannel = await $syncClient.map($syncChannels.onstage);

				roomSyncChannel.on('itemUpdated', (args) => {
					console.log('roomSyncChannel item updated', args.item, $settings.room);
					if ($settings.room && (args.item.key == $settings.room)) {
						updateRoomFromSync(args.item.data);
					}
				});

			} catch (e) {

				console.log("Sync error", e, $syncChannels.onstage);

			}

			try {

				console.log('sync -- attendee', $syncChannels.attendee);
				let attendeeNotifications = await $syncClient.list($syncChannels.attendee);

				attendeeNotifications.on('itemAdded', syncItemAdded);
				attendeeNotifications.on('itemRemoved', syncItemRemoved);

			} catch (e) {

				console.log("Sync error", e, $syncChannels.attendee);

			}

		}
	}

	function updateRoomFromSync(data) {
		console.log("updateRoomFromSync", {data});
		roomSyncData = data;
		if (!$session || (data.session != $session.ref)) {
			for (const s of $agenda.sessions) {
				if (s.ref == data.session) {
					$session = s;
				}
			}
		}
		if (data.spotlight && $session && (data.spotlight.session == $session.ref)) {
			$spotlight = data.spotlight;
		} else if (!data.spotlight) {
			$spotlight = null;
		}
	}

	function updateSyncFromSession() {
		if ($settings && $settings.room && roomSyncChannel) {
			if ($session && $session.ref && (roomSyncData.session != $session.ref)) {
				console.log('updateSyncFromSession', $session.ref, {roomSyncData});
				roomSyncChannel.mutate($settings.room, sessionMutator);
			}
		}
	}

	function updateSyncFromSpotlight() {
		if ($settings && $settings.room && roomSyncChannel) {
			if (($spotlight && !roomSyncData.spotlight) || (roomSyncData.spotlight && !$spotlight) || ($spotlight && roomSyncData.spotlight && ((roomSyncData.spotlight.type + roomSyncData.spotlight.index) != ($spotlight.type + $spotlight.index)))) {
				console.log('updateSyncFromSpotlight', $spotlight, {roomSyncData});
				roomSyncChannel.mutate($settings.room, spotlightMutator);
			}
		}
	}

	const sessionMutator = (currentData) => {
		currentData.session = $session.ref;
		return currentData;
	};

	const spotlightMutator = (currentData) => {
		currentData.spotlight = $spotlight;
		return currentData;
	};

	function setRoomFromSync() {
		console.log('setRoomFromSync');
		if ($settings && $settings.room && roomSyncChannel) {
			roomSyncChannel.get($settings.room)
				.then((room) => {
					updateRoomFromSync(room.data);	
				})
				.catch((e) => {
					if (String(e).includes('54201')) {
						updateSyncFromSession();
						updateSyncFromSpotlight();
					}
				}
			);
		}
	}

	$: updateSyncFromSession($session);
	$: updateSyncFromSpotlight($spotlight);
	$: setRoomFromSync($settings, roomSyncChannel);

	function upsertAttendee(item) {
		let a = item.data;
		a.ref = item.key;
		if (!a.seq) a.seq = item.dateUpdated.getTime();
		db.attendees.put(a);
		if (a.a) {
			preloadImage('https://cdn.attendzen.io/' + $event.accountRef + '/thumb_' + a.a);
		}
	}

	function attendeesPageHandler(paginator) {
		// paginator.items.forEach((item) => {
		// 	$attendees[item.key] = item.data;
		// 	$attendees[item.key].seq = $attendees[item.key].seq ? $attendees[item.key].seq : item.dateUpdated.getTime();
		// });
		// $attendees = $attendees;

		let bulk = [];

		paginator.items.forEach((item) => {
			bulk.push(item.data);
			if (item.data.a) {
				preloadImage('https://cdn.attendzen.io/' + $event.accountRef + '/thumb_' + item.data.a);
			}
		});

		db.attendees.bulkPut(bulk);

		return paginator.hasNextPage
			? paginator.nextPage().then(attendeesPageHandler)
			: null;

	}

	function getAttendance(attendance) {
		attendance.getItems({ pageSize: 100 })
			.then(attendeesPageHandler)
			.catch((error) => {
				if (error.message.match('Maximum attempt')) {
					// back off and try again later...
					const smear = 1000 * randomIntFromInterval(5,10);
					setTimeout(() => {
						getAttendance(attendance);
					}, smear);
				} else {
					console.error('getAttendance failed', error);
				}
			});
	}

	function syncItemAdded(message) {

		// It's message.item.data for lists and maps,
		// but message.data for documents...

		const messageData = message.item.data;

		console.log('notification', messageData);

		if (messageData && messageData.type && (messageData.type == 'system')) {

			if (messageData.hook && (messageData.hook == 'agenda')) {

				const smear = 1000 * randomIntFromInterval(0,30);
				// console.log('smearing', smear);
				setTimeout(async () => {
					const agendaData = await getServerData('onstage/agenda');
					if (!$bigError) {
						if (agendaData.agenda) setAgenda(agendaData.agenda);
					}
				}, smear);

			} else if (messageData.hook && (messageData.hook == 'settings')) {

				const smear = 1000 * randomIntFromInterval(0,10);
				// console.log('smearing', smear);
				setTimeout(async () => {
					const eventData = await getServerData('onstage/auth/settings');
					if (!$bigError) {
						if (eventData.event) setEvent(eventData.event);
					}
				}, smear);

			}

		} else if (messageData && messageData.type && (messageData.type == 'shadowban')) {
			if (!$shadowbans.includes(messageData.attendee)) {
				$shadowbans.push(messageData.attendee);
				$shadowbans = $shadowbans;
			}

		} else if (messageData && messageData.type && (messageData.type == 'unshadowban')) {
			$shadowbans = $shadowbans.filter(l => { l != messageData.attendee });

		} else if (messageData && messageData.type && (messageData.type == 'like')) {
			$attendeeLikes.push(messageData.hook);
			$attendeeLikes = $attendeeLikes;

		} else if (messageData && messageData.type && (messageData.type == 'unlike')) {
			$attendeeLikes = $attendeeLikes.filter(l => { l != messageData.hook });
		
		} else {

			// console.log('notification type', messageData.type);

		}

	}

	function syncItemRemoved(message) {

		const messageData = message.previousItemData;

		// console.log('notification removed', messageData);

	}

	function checkVisibility() {
		if (document.visibilityState == 'visible') {
			if (backgrounded) {
				let now = Date.now();
				let elapsed = now - backgrounded;
				// if (elapsed > 3600000) { // 1 hr in milliseconds
				if (elapsed > 60000) { // 1 minute in milliseconds
					// debugMessages.push('Returning from deep background (' + elapsed + ')');
					// debugMessages = debugMessages;
					const e = new Event("resume");
					window.dispatchEvent(e);
				}
				setRoomFromSync();
			}
		} else if (document.visibilityState == 'hidden') {
			backgrounded = Date.now();
		}		
	}

	function setRemSize() {
		document.documentElement.style.fontSize = $remSize;
	}

	$: if (twilioLoaded && syncToken) {
		twilioConnect();
	}

	$: if ($unlockCode) {
		sync(false);
	} else {
		resetEvent();
	}

	$: if ($remSize) {
		setRemSize();
	}

	// $: if ($deviceRef) {
	// 	Sentry.setUser($deviceRef);
	// }

</script>

<style>
	.wrapper {
		user-select: none;
	}
	.wrapper :global(input),
	.wrapper :global(textarea) {
		user-select: unset;
	}

	main {
		position: fixed;
		inset: 0;
		display: flex;
		flex-direction: column;
		/*text-align: center;*/
		box-sizing: border-box;
		margin: 0 auto;
		width: 100%;
		transition: background-color 0.2s ease;
		transition-delay: 0.2s;
		/*position: relative;*/
		padding: 0;
		background: var(--bgColor, #fff);
		color: var(--textColor, #000);
		min-height: 100dvh;
	}
	main :global(.inner) {
		max-width: 800px;
		margin: 0 auto;
		position: relative;
		height: 100%;
	}
	/*main :global(.inner > div) {
		padding: 0 1rem;
	}*/
	.content {
		flex: 1 0 auto;
		position: relative;
	}

	/* busy is now **above** overlays... */
	/*	but **below** modals */
	:global(#busy) {
		position: fixed;
		inset: 0;
		display: grid;
		place-content: center;
		z-index: 30000;
	}

	:global(#busy.modal) {
		z-index: 40000;
	}

	:global(#busy:before) {
		content: '';
		position: absolute;
		inset: 0;
		background: var(--bgColor);
		opacity: 0.75;
	}

	svg.azn {
		position: absolute;
		width: 80%;
		left: 50%;
		top: calc(50% - 4rem);
		transform: translate(-50%,-50%);
		max-width: 400px;
	}
	svg.azn .bubble {
		fill: var(--accentColor);
	}
	svg.azn .tick {
		fill: var(--textColor);
	}
	.go {
		position: absolute;
		width: 100%;
		top: auto;
		bottom: 5rem;
		display: none;
		padding: 0;
		max-width: 240px;
		left: 50%;
		transform: translateX(-50%);
	}
	.go.visible {
		display: block;
	}
</style>

<svelte:window on:resize={setIsNarrowScreen}/>

<svelte:head>
  	<link rel="stylesheet" href="https://www.attendzen.io/res/display/default/fonts/Inter/inter.css"/>
  	<link rel="stylesheet" href="https://res.attendzen.io/css/textcontent.css"/>
  	<script type="text/javascript" src="https://media.twiliocdn.com/sdk/js/sync/releases/3.0.1/twilio-sync.min.js" on:load={() => { twilioLoaded = true }}></script>
</svelte:head>

<div class="wrapper" use:cssVars={themeVars}>
	{#if mounted}

		<main bind:this={$appElement}>
			{#if $unlockCode}
				{#if $settings && $settings.room && ($settings.mode == 'av')}
					<Masthead/>
					{#if !$spotlight}
						{#if $session}
							<Holding/>
						{:else}
							<Now/>
						{/if}
					{/if}
				{:else}
					<Masthead/>
					<div class="content">
						<div class="inner">
							{#if $agenda && $agenda.sessions}
								<Now/>
							{/if}
						</div>
					</div>
				{/if}
			{:else}
				<svg class="azn" viewBox="0 0 180 180"><path d="m63.9 157.5c-1 0-2.1-.2-3-.8-2-1.1-3.3-3.2-3.3-5.5v-17.6c0-1.7-1.4-3.1-3.1-3.1h-20c-6.6 0-12-5.4-12-12v-84c0-6.6 5.4-12 12-12h111c6.6 0 12 5.4 12 12v84c0 6.6-5.4 12-12 12h-36.2c-1.2 0-2.3.3-3.2 1l-38.8 25c-1.1.7-2.2 1-3.4 1z" class="bubble"/><path d="m76.1 112.2c-1.2 0-2.3-.5-3.2-1.2l-17.8-15c-2-1.7-2.6-4.6-1.1-6.8 1.7-2.5 5.1-2.9 7.3-1l13.5 11.4c.4.4 1.1.3 1.4-.1l41.8-49.8c1.8-2.1 4.9-2.4 7-.6s2.4 4.9.6 7l-45.6 54.4c-.9 1-2.1 1.7-3.4 1.8-.1-.1-.3-.1-.5-.1z" class="tick"/></svg>
				<div class="go" class:visible={!showInstallMessage}>
					<Button label="Unlock" wide={true} tall={true} on:click={() => { $showSettings = true }}>
						<svg viewBox="0 0 80 80"><path d="M44 46c0-2.2-1.8-4-4-4s-4 1.8-4 4c0 1.5.8 2.8 2 3.4v5.6c0 1.1.9 2 2 2s2-.9 2-2v-5.5c1.2-.7 2-2 2-3.5zM57 34h-3v-5c0-7.7-6.3-14-14-14-6.4 0-11.7 4.3-13.4 10.1l3.7 1.7c1-4.5 5-7.8 9.7-7.8 5.5 0 10 4.5 10 10v5h-27c-2.2 0-4 1.8-4 4v23c0 2.2 1.8 4 4 4h34c2.2 0 4-1.8 4-4v-23c0-2.2-1.8-4-4-4zm0 25c0 1.1-.9 2-2 2h-30c-1.1 0-2-.9-2-2v-19c0-1.1.9-2 2-2h30c1.1 0 2 .9 2 2v19z"/></svg>
					</Button>
				</div>
			{/if}

			{#if $showSettings}
				<Settings on:reset={resetEvent}/>
			{/if}

			{#if showInstallMessage}
				<InstallMessage bind:showInstallMessage/>
			{/if}
		</main>

		{#if $bigError}
			<div id="busy" class="modal" in:fade|local={{ duration:200 }} out:fade|local={{ duration:200 }}>
				<BigErrorModal {mounted}/>
			</div>
		{:else if $modal}
			<div id="busy" class="modal" in:fade|local={{ duration:200 }} out:fade|local={{ duration:200 }}>
				<Modal/>
			</div>
		{:else if $busy}
			<div id="busy" in:fade|local={{ delay:600, duration:300 }} out:fade|local={{ duration:300 }}>
				<Spinner size="100" speed="1200" thickness="1" gap="30"/>
			</div>
		{/if}

	{:else}

		{#if $bigError}
			<div id="busy" class="modal" in:fade|local={{ duration:200 }} out:fade|local={{ duration:200 }}>
				<BigErrorModal {mounted}/>
			</div>
		{/if}

	{/if}
	{#if $spotlight && ((!$settings.room ) || ($settings.mode == 'av') || ($settings.displaySpotlight))}
		{#key $spotlight.type + $spotlight.index}
			<Spotlight/>
		{/key}
	{/if}
	<div id="dialog"></div>
</div>